import React from 'react';
import App from './App';
import { setConfig } from './configStore';
import { createContext } from '@wordpress/element';
import {
    createBrowserRouter,
    RouterProvider,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import './style/main.scss';
import * as Sentry from '@sentry/react';
import { ExtraErrorData } from '@sentry/integrations';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorComponent from './ErrorComponent';
import config from './config.json'; // Import the config file

// Import React rendering methods directly
import { createRoot as reactCreateRoot } from 'react-dom/client';
import { render as reactRender } from 'react-dom';

// Import WordPress rendering methods
import { createRoot as wpCreateRoot, render as wpRender, StrictMode } from '@wordpress/element';

export const ConfigContext = createContext(null);
export const ShortcodeContext = createContext(null);

/**
 * Centralized React application rendering function
 * Tries multiple methods to render the React app
 */
function initReactApplication(rootElement, app) {
  // Check for React directly
  const hasReact = typeof React !== 'undefined';
  const hasReactDOM = typeof ReactDOM !== 'undefined';

  // Try modern API first
  if (hasReactDOM && typeof ReactDOM.createRoot === 'function') {
    try {
      ReactDOM.createRoot(rootElement).render(app);
      return true;
    } catch (error) {
      console.warn('ReactDOM.createRoot failed:', error);
    }
  }
  
  // Try legacy render
  if (hasReactDOM && typeof ReactDOM.render === 'function') {
    try {
      ReactDOM.render(app, rootElement);
      return true;
    } catch (error) {
      console.warn('ReactDOM.render failed:', error);
    }
  }
  
  // Try WordPress element if available
  if (typeof wp !== 'undefined' && wp.element) {
    if (typeof wp.element.createRoot === 'function') {
      try {
        wp.element.createRoot(rootElement).render(app);
        return true;
      } catch (error) {
        console.warn('wp.element.createRoot failed:', error);
      }
    }
    
    if (typeof wp.element.render === 'function') {
      try {
        wp.element.render(app, rootElement);
        return true;
      } catch (error) {
        console.warn('wp.element.render failed:', error);
      }
    }
  }
  
  // No rendering method worked
  console.error('All React rendering methods failed - no compatible API found');
  return false;
}

/**
 * Get Sentry DSN per project based on URL.
 * DSN key in config should match live URL.
 * All dev sites will go to 'range-headless-dev' project.
 * If no matching project found, will go to 'range-headless'
 * @returns {string}
 */
function getSentryDsn() {
    const url = location.hostname.split('.');
    const fullHostname = location.hostname;

    if (fullHostname === 'amplifydispensary.websitefirstlook.com') {
        return config.sentryConfigs['mellocannabis'];
    }

    if (url.includes('test') || url[1] === 'websitefirstlook') {
        return config.sentryConfigs['range-headless-dev'];
    }

    let domain = '';
    if (url[0] === 'www') {
        domain = url[1];
    } else {
        domain = url[0];
    }

    if (Object.keys(config.sentryConfigs).includes(domain)) {
        return config.sentryConfigs[domain];
    }

    return config.sentryConfigs['range-headless'];
}

// Helper function to check if a shortcode should show the fallback message
function shouldShowFallback(shortcodeData) {
    // The list of shortcodes that should show the fallback message
    const fallbackEnabledShortcodes = ["headlessmenu", "headlessproducts"];

    // Check if this shortcode type is in our allowed list
    return shortcodeData &&
        shortcodeData.shortcode &&
        fallbackEnabledShortcodes.includes(shortcodeData.shortcode);
}

// Helper function to create the fallback HTML
function createFallbackHtml(alternateMenuUrl = '/backup-menu/') {
    return `
        <div class="headless-failsafe" style="text-align: center; padding: 20px; margin: 20px; border: 1px solid #ddd; border-radius: 5px; background-color: #f9f9f9; position:relative;z-index:9999;">
            <h3 style="color: #333; margin-bottom: 15px;">Not seeing our menu?</h3>
            <a target="_blank" href="${alternateMenuUrl}" style="display: inline-block; padding: 10px 20px; background-color: #4CAF50; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">Click Here</a>
        </div>
    `;
}

function initializeApp() {
    // Create a more robust Sentry initialization with better error handling
    try {
        const dsn = getSentryDsn();

        if (!dsn) {
            throw new Error('No valid Sentry DSN found');
        }

        // Define environment and release with fallbacks
        const environment = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development'
            ? 'development'
            : 'production';

        const release = typeof process !== 'undefined' && process.env && process.env.SENTRY_RELEASE
            ? process.env.SENTRY_RELEASE
            : 'range-headless-react@1.0.4'; // Fallback release name

        // More verbose initialization with debug option
        Sentry.init({
            environment: environment,
            dsn: dsn,
            release: release,
            integrations: [
                new Sentry.BrowserTracing({
                    routingInstrumentation: Sentry.reactRouterV6Instrumentation(
                        React.useEffect,
                        useNavigationType,
                        useLocation,
                        createRoutesFromChildren,
                        matchRoutes
                    ),
                }),
                new ExtraErrorData(),
            ],
            tracePropagationTargets: [/.+\/wp-json\/.+/, rangeHeadlessApi.apiEndpoint],
            tracesSampleRate: 0.2,
            sampleRate: 1, // This is causing 80% of errors to be discarded
            normalizeDepth: 4,

            // Add this tracesSampler to ensure test events are always captured
            tracesSampler: (samplingContext) => {
                // Always sample test events
                if (samplingContext.transactionContext?.tags?.test_type) {
                    return 1.0; // 100% sampling for test events
                }

                // Use default sampling rate for everything else
                return 1;
            },
        });
    } catch (sentryInitError) {
        console.error('Failed to initialize Sentry:', sentryInitError);
    }

    // Check for simulation flag in URL for testing
    const urlParams = new URLSearchParams(window.location.search);
    const simulateError = urlParams.get('simulateError') === 'true';

    if (simulateError) {
        console.log('Simulating React rendering error due to URL parameter');
        // Test Sentry configuration by sending a test error
        // Explicit test for Sentry with tags to ensure it's not sampled out
        try {
            const testError = new Error('Simulation test error for Sentry');
            testError.name = 'SimulatedReactRenderingError';

            // Use the Sentry.withScope method to add tags and ensure 100% sampling
            Sentry.withScope(scope => {
                // Set tags to identify this as a test
                scope.setTag('test_type', 'forced_sample');
                scope.setTag('url', window.location.href);

                // Force sampling rate via context
                scope.setContext('sampling', {
                    force_sample: true
                });

                // Set higher level to increase chance of sending
                scope.setLevel('fatal');

                // Capture the exception
                const eventId = Sentry.captureException(testError);
                console.log('Error sent to Sentry with event ID:', eventId);
            });
        } catch (sentryTestError) {
            console.error('Failed to send test error to Sentry:', sentryTestError);
        }
        try {
            const roots = document.querySelectorAll('[id^="range-headless-root-"]');
            if (roots.length > 0) {
                // Initialize data structures
                const shortcodeData = {};
                let globalConfig = null;
                let alternateMenuUrl = '/backup-menu/';

                // Collect shortcode data to determine which ones should show fallback
                roots.forEach((root) => {
                    try {
                        const rawData = root.dataset.rangeheadless;
                        if (rawData) {
                            shortcodeData[root.id] = JSON.parse(rawData);

                            // Try to get alternateMenuUrl from global config
                            if (shortcodeData[root.id].globalConfig &&
                                shortcodeData[root.id].globalConfig.alternateMenuUrl) {
                                alternateMenuUrl = shortcodeData[root.id].globalConfig.alternateMenuUrl;
                            }
                        }
                    } catch (error) {
                        console.error(`Error parsing data for root ${root.id}:`, error);
                    }
                });

                // Apply fallback to eligible shortcodes
                let fallbackCount = 0;
                roots.forEach(rootElement => {
                    const rootId = rootElement.id;
                    const data = shortcodeData[rootId];

                    if (shouldShowFallback(data)) {
                        rootElement.innerHTML = createFallbackHtml(alternateMenuUrl);
                        fallbackCount++;
                    }
                });

                console.log(`Displayed simulated fallback message for ${fallbackCount} eligible shortcodes`);
            }
        } catch (e) {
            console.error('Failed to simulate fallback message:', e);
        }
        return; // Exit early to prevent normal initialization
    }

    try {
        // Clear any potentially problematic cached data
        if (localStorage.getItem('range-react-error')) {
            console.log('Previous React errors detected, clearing possible problematic cache data');

            // Clear only application-specific data that might affect React initialization
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith("headless_") || key.includes("react") || key.includes("DUTCHIE")) {
                    localStorage.removeItem(key);
                }
            });
        }
    } catch (e) {
        // Ignore localStorage errors
    }
    try {
        const roots = document.querySelectorAll('[id^="range-headless-root-"]');
        if (!roots.length) {
            console.warn('No Range Headless root elements found');
            return;
        }

        // Initialize data structures
        const shortcodeData = {};
        let globalConfig = null;

        // First pass: collect all data and find global config
        roots.forEach((root) => {
            try {
                const rawData = root.dataset.rangeheadless;
                if (!rawData) {
                    throw new Error(`No data found for root ${root.id}`);
                }

                const parsedData = JSON.parse(rawData);

                // Store shortcode data
                shortcodeData[root.id] = parsedData;

                // If this shortcode has global config, store it
                if (parsedData.hasGlobal && parsedData.globalConfig) {
                    globalConfig = parsedData.globalConfig;
                }
            } catch (error) {
                Sentry.captureException(error, {
                    extra: {
                        rootId: root.id,
                        dataAttribute: root.dataset.rangeheadless
                    }
                });
            }
        });

        // If no global config was found, try to fetch it from the first shortcode
        if (!globalConfig && Object.keys(shortcodeData).length > 0) {
            const firstShortcodeData = Object.values(shortcodeData)[0];
            if (firstShortcodeData.globalConfig) {
                globalConfig = firstShortcodeData.globalConfig;
                setConfig(globalConfig);
            } else {
                console.error('No global config found in any shortcode');
                return;
            }
        } else if (globalConfig) {
            setConfig(globalConfig);
        }

        // Initialize React Query
        const queryClient = new QueryClient({
            defaultOptions: {
                queries: {
                    retry: (failureCount, error) => {
                        // Don't retry on 429 errors
                        if (error?.response?.status === 429) return false;
                        return failureCount < 2; // Only retry twice for other errors
                    },
                    staleTime: 60 * 1000, // Reduce to 1 minute for specials-related queries
                    cacheTime: 30 * 60 * 1000, // 30 minutes
                    refetchOnWindowFocus: false, // Prevent refetching when window gets focus
                    refetchOnReconnect: false, // Prevent refetching on network reconnection
                }
            }
        });

        // Create router
        const router = createBrowserRouter([
            {
                path: '/:viewName?/:locationSlug?/:categoryType?/:categoryName?/:specialCategoryName?',
                element: (
                    <Sentry.ErrorBoundary
                        fallback={(props) => <ErrorComponent {...props} />}
                        onError={(error) => {
                            Sentry.captureException(error, {
                                extra: {
                                    shortcodeData,
                                    globalConfig
                                }
                            });
                        }}
                    >
                        <ConfigContext.Provider value={globalConfig}>
                            <ShortcodeContext.Provider value={shortcodeData}>
                                <App
                                    shortcodeData={shortcodeData}
                                    globalConfig={globalConfig}
                                    fallback={rangeHeadlessApi.fallback}
                                />
                            </ShortcodeContext.Provider>
                        </ConfigContext.Provider>
                    </Sentry.ErrorBoundary>
                ),
            },
        ]);

        // Only render to first root
        if (roots.length > 0) {
            const rootElement = roots[0];

            const app = (
                <StrictMode>
                    <QueryClientProvider client={queryClient}>
                        <RouterProvider router={router} />
                    </QueryClientProvider>
                </StrictMode>
            );

            // Use the new unified rendering function that tries multiple methods
            const renderSuccess = initReactApplication(rootElement, app);
            
            // If rendering failed, show fallback message
            if (!renderSuccess) {
                try {
                    // Get alternate menu URL from global config
                    let alternateMenuUrl = '/backup-menu/';
                    if (globalConfig && globalConfig.alternateMenuUrl) {
                        alternateMenuUrl = globalConfig.alternateMenuUrl;
                    }

                    // Apply fallback to eligible shortcodes only
                    let fallbackCount = 0;
                    roots.forEach(rootEl => {
                        const rootId = rootEl.id;
                        const data = shortcodeData[rootId];

                        if (shouldShowFallback(data)) {
                            rootEl.innerHTML = createFallbackHtml(alternateMenuUrl);
                            fallbackCount++;
                        }
                    });

                    console.log(`Displayed fallback message for ${fallbackCount} eligible shortcodes`);

                    // Record this failure in localStorage for future detection
                    try {
                        localStorage.setItem('range-react-error', 'true');
                    } catch (e) {
                        // Ignore localStorage errors
                    }
                } catch (fallbackMessageError) {
                    console.error('Failed to display fallback message:', fallbackMessageError);
                }
            }
        }
    } catch (error) {
        try {
            localStorage.setItem('range-react-error', 'true');
        } catch (e) {
            // Ignore
        }
        Sentry.captureException(error);
        console.error('Failed to initialize Range Headless:', error);

        // Add fallback handling for catastrophic errors
        try {
            const roots = document.querySelectorAll('[id^="range-headless-root-"]');
            if (roots.length > 0) {
                // Try to gather shortcode data if possible
                const shortcodeData = {};
                let alternateMenuUrl = '/backup-menu/';

                // Attempt to collect shortcode data
                roots.forEach((root) => {
                    try {
                        const rawData = root.dataset.rangeheadless;
                        if (rawData) {
                            shortcodeData[root.id] = JSON.parse(rawData);

                            // Try to get alternateMenuUrl from global config
                            if (shortcodeData[root.id].globalConfig &&
                                shortcodeData[root.id].globalConfig.alternateMenuUrl) {
                                alternateMenuUrl = shortcodeData[root.id].globalConfig.alternateMenuUrl;
                            }
                        }
                    } catch (e) {
                        // Ignore parsing errors
                    }
                });

                // Apply fallback to eligible shortcodes
                let fallbackCount = 0;
                roots.forEach(rootElement => {
                    const rootId = rootElement.id;
                    const data = shortcodeData[rootId];

                    if (shouldShowFallback(data)) {
                        rootElement.innerHTML = createFallbackHtml(alternateMenuUrl);
                        fallbackCount++;
                    }
                });

                console.log(`Displayed fallback message for ${fallbackCount} eligible shortcodes due to catastrophic error`);
            }
        } catch (e) {
            // Last resort failed, nothing else we can do
            console.error('All fallback mechanisms failed:', e);
        }
    }
}

// Initialize the app
initializeApp();
